import { Injectable, inject } from '@angular/core';
import { Resolve, ActivatedRouteSnapshot } from '@angular/router';
import { Observable, combineLatest, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { MainNavigationService } from '../main-navigation.service';
import { NGXLogger } from 'ngx-logger';

export interface TeaByTypeResolverData {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  teaType: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  teas: any[];
  errors: string[];
  teaLadyAssetPath: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class TeaByTypeResolver implements Resolve<TeaByTypeResolverData> {
  private mainNavigationService = inject(MainNavigationService);
  private logger = inject(NGXLogger);


  resolve(route: ActivatedRouteSnapshot): Observable<TeaByTypeResolverData> {
    // Get tea type slug from route data or params
    const teaTypeSlug = route.data['teaTypeSlug'] || route.params['tea-type-slug'];
    
    if (!teaTypeSlug) {
      return of({
        teaType: null,
        teas: [],
        errors: ['Tea type slug not found in route'],
        teaLadyAssetPath: null
      });
    }

    // Combine both API calls
    return combineLatest([
      this.mainNavigationService.getTeaType(teaTypeSlug).pipe(
        catchError(error => {
          this.logger.error('An error occurred while retrieving the tea type:', error);
          return of(null);
        })
      ),
      this.mainNavigationService.getTeasByType(teaTypeSlug).pipe(
        catchError(error => {
          this.logger.error('An error occurred while retrieving the teas:', error);
          return of([]);
        })
      )
    ]).pipe(
      map(([teaType, teas]) => {
        const errors: string[] = [];
        
        if (!teaType) {
          errors.push('An error occurred while retrieving the tea type.');
        }
        
        if (!Array.isArray(teas) || teas.length === 0) {
          // Only add error if we expected teas but got none due to an error
          if (!Array.isArray(teas)) {
            errors.push('An error occurred while retrieving the teas.');
          }
        }

        // Determine tea lady asset path
        let teaLadyAssetPath: string | null = null;
        if (teaType && !['matcha', 'tea-wares'].includes(teaTypeSlug)) {
          if (teaTypeSlug === 'black-tea-blends') {
            teaLadyAssetPath = '/assets/tea-type-lady/black-tea-lady.png';
          } else  {
            teaLadyAssetPath = `/assets/tea-type-lady/${teaTypeSlug}-lady.png`;
          }
        }

        return {
          teaType,
          teas: Array.isArray(teas) ? teas : [],
          errors,
          teaLadyAssetPath
        };
      })
    );
  }
}
